/**
 * ResponseTrendChart — Multi-line response time trend (one line per area type, 4 weeks).
 * Each area record has: area_type_name, week1, week2, week3, week4
 */

import { useMemo, useState, useRef } from 'react';
import LineTrendingChart from '../../../../components/charts/LineTrendingChart.jsx';
import { formatDateNoYear, getToday } from '../../../../libs.jsx';

const COLORS = ['#2C7BE5', '#392CE5', '#05B2FF', '#A6C5F7', '#8989FF', '#D2DDEC', '#E52C7B', '#962CE5', '#E52CD8'];

const TREND_TOOLTIP = [
    'Displays the average response time in minutes for each 7-day period',
    'over the previous 4 weeks, broken down by care type. Click a care type',
    'in the legend to show or hide it on the graph.',
];

/* ── TrendInfoTip — ⓘ icon with hover popover ── */
const TrendInfoTip = () => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const [style, setStyle] = useState({});

    const handleEnter = () => {
        if (!ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        const spaceRight = window.innerWidth - rect.left;
        const spaceBelow = window.innerHeight - rect.bottom;

        setStyle({
            position: 'fixed',
            left: spaceRight < 320 ? undefined : `${rect.left}px`,
            right: spaceRight < 320 ? '16px' : undefined,
            top: spaceBelow < 80 ? undefined : `${rect.bottom + 6}px`,
            bottom: spaceBelow < 80 ? `${window.innerHeight - rect.top + 6}px` : undefined,
            maxWidth: '300px',
            whiteSpace: 'normal',
        });
        setOpen(true);
    };

    return (
        <span ref={ref} onMouseEnter={handleEnter} onMouseLeave={() => setOpen(false)} style={{ cursor: 'help', marginLeft: '6px', color: 'var(--color-text-muted)' }}>
            <i className="fe fe-info" style={{ fontSize: 'var(--font-size-sm)' }} />
            {open && (
                <span className="na-type-popover" style={style}>
                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontWeight: 'var(--font-weight-semibold)' }}>About This Chart</span>
                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-body)', lineHeight: '1.45' }}>{TREND_TOOLTIP.join(' ')}</span>
                </span>
            )}
        </span>
    );
};

const ResponseTrendChart = ({ areaData }) => {
    const chartProps = useMemo(() => {
        if (!Array.isArray(areaData) || areaData.length === 0) return null;

        // Week labels (oldest to newest, matching V1 order: week4, week3, week2, week1)
        const labels = [
            `${formatDateNoYear(getToday(-28))}–${formatDateNoYear(getToday(-21))}`,
            `${formatDateNoYear(getToday(-21))}–${formatDateNoYear(getToday(-14))}`,
            `${formatDateNoYear(getToday(-14))}–${formatDateNoYear(getToday(-8))}`,
            `${formatDateNoYear(getToday(-8))}–${formatDateNoYear(getToday(-1))}`,
        ];

        // Each area type becomes a line: [week4, week3, week2, week1]
        const lineData = areaData.map((at) => [at.week4, at.week3, at.week2, at.week1]);
        const lineLabels = areaData.map((at) => at.area_type_name || '');
        const lineColors = areaData.map((_, i) => COLORS[i % COLORS.length]);

        return { labels, lineData, lineLabels, lineColors, lines: areaData.length };
    }, [areaData]);

    if (!chartProps) {
        return (
            <div className="md-section">
                <h3 className="md-section__title">Response Time Trends<TrendInfoTip /></h3>
                <div style={{ textAlign: 'center', padding: 'var(--space-6)', color: 'var(--color-text-muted)' }}>No trend data available</div>
            </div>
        );
    }

    return (
        <div className="md-section">
            <h3 className="md-section__title" style={{ marginBottom: 'var(--space-4)' }}>Response Time Trends<TrendInfoTip /></h3>
            <div className="md-chart-container">
                <LineTrendingChart
                    style={{ maxHeight: '350px' }}
                    x_axis={true}
                    x_axis_title="Weeks"
                    line_axis={true}
                    line_axis_title="Response Time (min)"
                    lines={chartProps.lines}
                    line_color={chartProps.lineColors}
                    line_labels={chartProps.lineLabels}
                    line_data={chartProps.lineData}
                    forceAspect={true}
                    labels={chartProps.labels}
                />
            </div>
        </div>
    );
};

export default ResponseTrendChart;
